<script setup lang="ts">
import { useFetch, useRuntimeConfig, useState } from '#app'
import Slider from 'primevue/slider'
import FlatStatusChip from '~/components/flatStatusChip.vue'
import type { Flat, FlatPriceHistory, FlatStatus } from '~/types/types'
import { computed, ref, watchEffect, onMounted } from 'vue'
import PriceHistoryDialog from '~/components/PriceHistoryDialog.vue'
import type { CmsCustomComponentType } from '~/layers/cms/app/types/cmsTypes'
import AppButton from '~/components/AppButton.vue'
import { getLokaleWithHistoryNajem } from '#imports'

const Flats = useState<Record<number, Flat>>('Flats')

const config = useRuntimeConfig()
const { data } = await useFetch(`${config.public.apiBase}/api/get-apartamenty-with-price-change-history-najem`, {
  transform: response => (response ?? []) as Flat[],
  getCachedData: (key, nuxtApp) =>
    nuxtApp.isHydrating ? nuxtApp.payload.data[key] : undefined
})

watchEffect(() => {
  if (!data.value) return
  Flats.value = Object.fromEntries(data.value.map(f => [f.id, f]))
})

onMounted(async () => {
  const res = await getLokaleWithHistoryNajem()
  if (res.status_ok) {
    data.value = res.data as Flat[]
    Flats.value = Object.fromEntries((res.data as Flat[]).map(f => [f.id, f]))
  }
})

const flat_types = computed(() =>
  [...new Set(data.value?.map(item => item.type).filter((v): v is string => v !== null && v !== undefined))]
)

const flat_statuses = computed(() =>
  [...new Set(data.value?.map(item => item.status_name).filter((v): v is string => v !== null && v !== undefined))]
)

const flat_rooms = computed(() =>
  [...new Set(data.value?.map(item => item.rooms).filter((v): v is number => v !== null && v !== undefined))]
    .sort((a, b) => a - b)
)

const flat_floors = computed(() =>
  [...new Set(data.value?.map(item => item.floor).filter((v): v is number => v !== null && v !== undefined))]
    .sort((a, b) => a - b)
)
const areas = computed(() =>
  data.value?.map(item => item.area).filter((area): area is number => area !== null) ?? []
)

const max_area = computed(() =>
  Math.max(...areas.value)
)

const min_area = computed(() =>
  Math.min(...areas.value)
)

const percent = (value: number) => ((value - min_area.value) / (max_area.value - min_area.value)) * 100

const choosenType = ref<string>('Wszystkie')
const choosenStatus = ref<string>('Wszystkie')
const choosenRooms = ref<number[]>([])
const choosenFloors = ref<number[]>([])

const toggleRoomFilter = (number: number) => {
  const index = choosenRooms.value.indexOf(number)
  if (index === -1) {
    choosenRooms.value.push(number)
  } else {
    choosenRooms.value.splice(index, 1)
  }
}

const toggleFloorFilter = (number: number) => {
  const index = choosenFloors.value.indexOf(number)
  if (index === -1) {
    choosenFloors.value.push(number)
  } else {
    choosenFloors.value.splice(index, 1)
  }
}

const area_ranges = ref<[number, number]>([min_area.value, max_area.value])

const current_show_number = ref<number>(12)

const filterFlats = (flats: Flat[], choosenType: string, choosenStatus: string, choosenRooms: number[], choosenFloors: number[], area_ranges: [number, number]) => {
  let filterd_flats = flats
  if (choosenType != 'Wszystkie') {
    filterd_flats = filterd_flats.filter(f => f.type == choosenType)
  }
  if (choosenStatus != 'Wszystkie') {
    filterd_flats = filterd_flats.filter(f => f.status_name == choosenStatus)
  }
  if (choosenRooms.length > 0) {
    filterd_flats = filterd_flats.filter(f => choosenRooms.includes(f.rooms as number))
  }
  if (choosenFloors.length > 0) {
    filterd_flats = filterd_flats.filter(f => choosenFloors.includes(f.floor as number))
  }
  filterd_flats = filterd_flats.filter(f => (f.area as number) >= area_ranges[0] && (f.area as number) <= area_ranges[1])
  return filterd_flats
}

const filtered_flats = computed(() => {
  return filterFlats(
    data.value ?? [],
    choosenType.value,
    choosenStatus.value,
    choosenRooms.value,
    choosenFloors.value,
    area_ranges.value
  ).slice(0, current_show_number.value)
})

const max_show_number = computed(() =>
  filterFlats(data.value ?? [], choosenType.value, choosenStatus.value, choosenRooms.value, choosenFloors.value, area_ranges.value).length
)

function loadMore() {
  if (current_show_number.value >= max_show_number.value) return
  current_show_number.value += 12
}

const dialogOpen = ref<boolean>(false)
const choosenflat_price_history = ref<null | FlatPriceHistory[]>(null)

const props = defineProps<{
  cmsDataPath: (string | number)[]
}>()
const nazwa_komponentu = 'Special/SprzedazMieszkanSearchNajem'
const component_default_values: CmsCustomComponentType = {
  id: { value: undefined, default_value: 'Lorem Ipsum', settingType: 'text', setting_label: 'zmien id' }
}
</script>

<template>
  <CmsCustomComponent
    v-slot="{ component_values }"
    :cms-data-path="[...props.cmsDataPath]"
    :component-name="nazwa_komponentu"
    :defaults="component_default_values"
    :open-options="true"
  >
    <PriceHistoryDialog
      :open="dialogOpen"
      :flat-price-history="choosenflat_price_history"
      @close="dialogOpen = !dialogOpen"
    />
    <div
      :id="component_values?.id?.value"
      class="py-10 px-2xl flex flex-col gap-15 bg-background-1"
    >
      <span class="text-H2 text-Tekst">Wyszukaj mieszkanie</span>
      <div class="flex flex-col 2xl:flex-row gap-8">
        <div class="flex flex-col gap-3 xl:gap-8">
          <span class="text=Paragraph text-Tekst font-bold">TYP</span>
          <div class="relative w-60">
            <select
              id="typ"
              v-model="choosenType"
              name="typ"
              class="py-1 xl:py-2.5 pl-5 pr-10 border border-Tekst rounded-full appearance-none bg-none truncate w-full"
            >
              <option
                value="Wszystkie"
                selected
              >
                Wszystkie
              </option>
              <option
                v-for="flat_type in flat_types"
                :key="JSON.stringify(flat_type)"
                :value="flat_type"
              >
                {{ flat_type }}
              </option>
            </select>
            <i class="pi pi-chevron-down text-[20px]! text-Tekst absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>
        <div class="flex flex-col gap-3 xl:gap-8">
          <span class="text=Paragraph text-Tekst font-bold">Status</span>
          <div class="relative w-60">
            <select
              id="typ"
              v-model="choosenStatus"
              name="typ"
              class="py-1 xl:py-2.5 pl-5 pr-10 border border-Tekst rounded-full appearance-none bg-none truncate w-full"
            >
              <option
                value="Wszystkie"
                selected
              >
                Wszystkie
              </option>
              <option
                v-for="flat_status in flat_statuses"
                :key="JSON.stringify(flat_status)"
                :value="flat_status"
              >
                {{ flat_status }}
              </option>
            </select>
            <i class="pi pi-chevron-down text-[20px]! text-Tekst absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>
        <div class="flex flex-col gap-3 xl:gap-8">
          <span class="text=Paragraph text-Tekst font-bold">Piętro</span>
          <div class="flex flex-row gap-2.5">
            <div
              v-for="floor in flat_floors"
              :key="JSON.stringify(floor)"
              class="rounded-full w-8 h-8 border border-Tertiary flex items-center justify-center cursor-pointer"
              :class="choosenFloors.includes(floor) ? 'bg-Tertiary text-white' : 'text-Tekst'"
              @click="toggleFloorFilter(floor as number)"
            >
              <span class="text-Paragraph">{{ floor }}</span>
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-3 xl:gap-8">
          <span class="text=Paragraph text-Tekst font-bold">Liczba&nbsp;pokoi</span>
          <div class="flex flex-row gap-2.5">
            <div
              v-for="room in flat_rooms"
              :key="JSON.stringify(room)"
              class="rounded-full w-8 h-8 border border-Tertiary flex items-center justify-center cursor-pointer"
              :class="choosenRooms.includes(room) ? 'bg-Tertiary text-white' : 'text-Tekst'"
              @click="toggleRoomFilter(room as number)"
            >
              <span class="text-Paragraph select-none">{{ room }}</span>
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-3 xl:gap-8 w-full">
          <span class="text=Paragraph text-Tekst font-bold">Metraż</span>
          <div class="flex flex-row gap-4 items-end justify-end">
            <span class="text-Paragraph text-Secondary">{{ min_area }}m<sup>2</sup></span>
            <div class="grid grid-cols-1 grid-rows-1 w-full">
              <div class="row-start-1 row-end-1 col-start-1 col-end-1 relative">
                <div class="w-4 h-4 bg-Secondary rounded-full absolute top-4.5 -left-1.5" />
              </div>
              <div class="row-start-1 row-end-1 col-start-1 col-end-1 relative">
                <div class="w-4 h-4 bg-Secondary rounded-full absolute top-4.5 -right-1.5" />
              </div>
              <div class="row-start-1 row-end-1 col-start-1 col-end-1 w-full">
                <div class="relative w-full pt-6 h-10">
                  <span
                    class="absolute -top-2 -translate-x-1/2 text-Paragraph text-Tekst"
                    :style="{ left: `${percent(area_ranges[0])}%` }"
                  >
                    {{ area_ranges[0] }}m²
                  </span>
                  <span
                    class="absolute -top-2 -translate-x-1/2 text-Paragraph text-Tekst"
                    :style="{ left: `${percent(area_ranges[1])}%` }"
                  >
                    {{ area_ranges[1] }}m²
                  </span>
                  <Slider
                    v-model="area_ranges"
                    range
                    :min="min_area"
                    :max="max_area"
                    class="mx-auto w-full"
                    :pt="{
                      root: { class: 'bg-Secondary!' },
                      track: { class: 'h-2! bg-Secondary!' },
                      range: { class: 'bg-Tekst!' },
                      handle: { class: 'before:hidden! before:w-2! before:h-2! border-0! bg-Tekst! rotate-45! rounded-md! w-2! h-2!' }
                    }"
                  />
                </div>
              </div>
            </div>
            <span class="text-Paragraph text-Secondary">{{ max_area }}m<sup>2</sup></span>
          </div>
        </div>
      </div>
      <div class="w-full">
        <div class="w-full overflow-x-auto">
          <table class="w-full border-collapse">
            <thead class="rounded-full">
              <tr class="rounded-full bg-Tekst text-Paragraph text-white font-normal">
                <th class="text-center py-1 xl:py-2.5 px-4 xl:px-10 rounded-l-full w-80">
                  Typ
                </th>
                <th class="text-center py-1 xl:py-2.5 px-4 xl:px-10">
                  Numer
                </th>
                <th class="text-center py-1 xl:py-2.5 px-4 xl:px-10">
                  Piętro
                </th>
                <th class="text-center py-1 xl:py-2.5 px-4 xl:px-10">
                  Pokoje
                </th>
                <th class="text-center py-1 xl:py-2.5 px-4 xl:px-10">
                  Metraż
                </th>
                <th class="text-center py-1 xl:py-2.5 px-4 xl:px-10">
                  Cena
                </th>
                <th class="text-center py-1 xl:py-2.5 px-4 xl:px-10">
                  Status
                </th>
                <th class="text-center py-1 xl:py-2.5 px-4 xl:px-10 rounded-r-full">
                  Rzut
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="unit in filtered_flats"
                :key="JSON.stringify(unit)"
                class="border-b border-Tertiary last:border-0 text-center text-Tekst text-Paragraph text-nowrap"
              >
                <td class="py-1 xl:py-2.5">
                  {{ unit.type ?? '-' }}
                </td>
                <td class="py-1 xl:py-2.5">
                  {{ unit.administrative_number ?? '-' }}
                </td>
                <td class="py-1 xl:py-2.5">
                  {{ unit.floor ?? '-' }}
                </td>
                <td class="py-1 xl:py-2.5">
                  {{ unit.rooms ?? '-' }}
                </td>
                <td class="py-1 xl:py-2.5">
                  {{ unit.area ? `${unit.area} m²` : '-' }}
                </td>
                <td class="py-1 xl:py-2.5">
                  <div class="flex items-center justify-center">
                    <a
                      href="#kontakt"
                      class="flex flex-row gap-1 items-center py-2.5 px-6 border border-Secondary rounded-full w-fit"
                    >
                      <span class="text-Paragraph text-Tekst">Dowiedz się</span>
                    </a>
                  </div>
                </td>

                <td class="py-1 xl:py-2.5 flex items-center justify-center">
                  <FlatStatusChip :status="(unit.status_name as FlatStatus)" />
                </td>

                <td class="py-1 xl:py-2.5">
                  <div class="flex items-center justify-center">
                    <a
                      v-if="unit.plan_link"
                      :href="unit.plan_link"
                      :download="unit.plan_link.split('/').pop()"
                      target="_blank"
                      class="flex flex-row gap-1 items-center py-2.5 px-6 border border-Secondary rounded-full w-fit"
                    >
                      <span class="text-Paragraph text-Tekst">Pobierz rzut</span>
                      <NuxtImg
                        src="https://api.ostoja-seniora.pl/files/plan-icon.svg"
                        height="18"
                        wifth="17"
                        loading="lazy"
                        alt="plan"
                        class="object-contain w-4.5 h-4.5"
                      />
                    </a>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div
            v-if="filtered_flats.length <= 0"
            class="w-full text-center p-10"
          >
            <span class="text-Paragraph text-Tekst text-center">Brak mieszkań</span>
          </div>
        </div>
        <div class="flex justify-center py-6">
          <AppButton
            v-if="current_show_number < max_show_number"
            button-theme="Transparent-Teksty"
            @click="loadMore"
          >
            <span>Doładuj więcej</span>
          </AppButton>
        </div>
      </div>
    </div>
  </CmsCustomComponent>
</template>
